import { useMemo } from "react";
import { useAuthorization } from "~/contexts/authorizationProvider";
import { DocumentsSettingsPermission } from "~/shared/types/RBACPermission";
import { createLeadingPathRegex } from "~/utils/paths";
import { isOptInOrderDocumentSettingsNavVisible } from "./gating";
import { OPT_IN_ORDER_DOCUMENT_DEFINITIONS } from "./registry";
import type { GetOrgSetting } from "./types";

export type OptInOrderDocumentSettingsNavLink = {
  label: string;
  href: string;
  activeRegex: RegExp;
  searchWords: readonly string[];
};

/**
 * Settings → Documents nav links for enabled opt-in order documents.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
export const useOptInOrderDocumentSettingsNavLinks = (
  getOrgSetting: GetOrgSetting,
  orgBaseCurrencyCode: string,
): OptInOrderDocumentSettingsNavLink[] => {
  const { hasPermission } = useAuthorization();
  const canManageDocumentsSettings = hasPermission([DocumentsSettingsPermission.update]);

  return useMemo(
    () =>
      OPT_IN_ORDER_DOCUMENT_DEFINITIONS.flatMap(definition => {
        if (!canManageDocumentsSettings || !isOptInOrderDocumentSettingsNavVisible(definition, getOrgSetting, orgBaseCurrencyCode)) {
          return [];
        }
        const settingsNav = definition.settingsNav;
        if (!settingsNav) {
          return [];
        }
        return [
          {
            label: settingsNav.label,
            href: settingsNav.href,
            activeRegex: createLeadingPathRegex(settingsNav.href, { wildcardPathEnd: true }),
            searchWords: settingsNav.searchWords,
          },
        ];
      }),
    [canManageDocumentsSettings, getOrgSetting, orgBaseCurrencyCode],
  );
};
